import { Injectable, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { CrewBonusPercentageResponse } from '../../sdk/sdk';
import { CalendarService } from '../../services/calendar.service';

export interface CrewBonusRow {
  userId: string;
  name: string;
  role: number | null;
  bonusPercent: number | null;
  isOverride: boolean;
}

/**
 * Owns the Preferred Crew bonus grid's state (MOD-4627).
 *
 * Holds every eligible crew option the server returned, not only the ticked ones, so the grid
 * repaints the instant someone is added or removed without a refetch mid-edit.
 *
 * It does not decide what counts as an override. The save sends every visible row and the server
 * compares each against the person's role default, because the client is deliberately never told
 * what those defaults are.
 */
@Injectable()
export class CrewBonusStore {
  private readonly calendarService = inject(CalendarService);

  private readonly rows = signal<CrewBonusRow[]>([]);
  private readonly edits = signal<Map<string, number | null>>(new Map());

  load(serverRows: CrewBonusPercentageResponse[]): void {
    this.rows.set(
      serverRows.map((row) => ({
        userId: row.userId,
        name: row.name ?? '',
        role: row.role ?? null,
        bonusPercent: row.bonusPercent ?? null,
        isOverride: row.isOverride,
      })),
    );
    this.edits.set(new Map());
  }

  /**
   * The foreman first, then the ticked crew, in the order the server returned them. The foreman
   * is unioned into `selectedUserIds` here — they're always visible in the grid regardless of who
   * else is picked, so this is the one place that decides who's on screen. `saveChanges` builds
   * off this same method so the save can never drift from what the user can actually see and edit.
   */
  visibleRows(selectedUserIds: string[], foremanUserId: string): CrewBonusRow[] {
    const visible = new Set([foremanUserId, ...selectedUserIds]);
    const rows = this.rows()
      .filter((row) => visible.has(row.userId))
      .map((row) => ({ ...row, bonusPercent: this.currentPercent(row) }));

    return [
      ...rows.filter((row) => row.userId === foremanUserId),
      ...rows.filter((row) => row.userId !== foremanUserId),
    ];
  }

  setPercent(userId: string, value: number | null): void {
    this.edits.update((current) => new Map(current).set(userId, value));
  }

  /** Sends every row `visibleRows` would currently show — the foreman plus the selected crew. */
  saveChanges(foremanUserId: string, selectedUserIds: string[]): Observable<void> {
    const rows = this.visibleRows(selectedUserIds, foremanUserId).map((row) => ({
      userId: row.userId,
      bonusPercent: row.bonusPercent,
    }));

    return rows.length === 0
      ? of(undefined)
      : this.calendarService.saveCrewBonusPercentages(foremanUserId, rows);
  }

  private currentPercent(row: CrewBonusRow): number | null {
    return this.edits().has(row.userId) ? (this.edits().get(row.userId) ?? null) : row.bonusPercent;
  }
}
